import React, { useState, useEffect } from "react";
import {
    Grid,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    CircularProgress,
} from "@material-ui/core";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import TableComponent from "./TableComponent.js";
import { BACKEND_BASE_URL } from "../../config.js";
import api from '../../components/api';
import { Pagination } from "@mui/material";

export default function Tables() {
    const [data, setData] = useState([]); // Initialize empty data array
    const [filteredData, setFilteredData] = useState([]); // State for filtered data
    const [searchQuery, setSearchQuery] = useState(""); // Search query state
    const [sortKey, setSortKey] = useState(""); // Default sorting by date (recent first)
    const [uploadStatus, setUploadStatus] = useState(null); // Upload status
    const [uploadMessage, setUploadMessage] = useState(""); // Upload message
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false); // Loading state for uploads
    const [isDeleting, setIsDeleting] = useState(false); // Prevents upload issue after delete
    const token = localStorage.getItem("authToken");
    const [mappedData, setMappedData] = useState([])
    const [liveCitiesCount, setLiveCitiesCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 200;
    const [searchLoading, setSearchLoading] = useState(false);
    const [apiKeys, setApiKeys] = useState([]);
    const [selectedApiKey, setSelectedApiKey] = useState('');


    useEffect(() => {
        axios
            .get(`${BACKEND_BASE_URL}/api/municipalities/unique_municipalities/`)
            .then((res) => {
                setLiveCitiesCount(res?.data.total_unique_count);

            })
            .catch((err) => {
                console.error("Error fetching live cities count:", err);
            });
    }, []);

    const fetchAPIKeys = async () => {
        try {
            const response = await axios.get(`${BACKEND_BASE_URL}/api/youtube-api-keys/`);
            const keys = response?.data;
            setApiKeys(keys);

            // Set default to first key if available
            if (keys.length > 0) {
                setSelectedApiKey(keys[0].api_key);
            }
        } catch (error) {
            toast.error('Failed to fetch API keys.', 'error');
        }
    };


    useEffect(() => {
        fetchUpdatedData();
        fetchMappedData();
        fetchAPIKeys();
    }, [currentPage]);


    const fetchMappedData = async () => {
        setLoading(true);
        try {
            const response = await api.get(`${BACKEND_BASE_URL}/api/mapping-towns/list_small_towns/`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const mappedData = response.data;


            setMappedData(mappedData)
            // Default sorting (recent first)

        } catch (error) {
            console.error("Error fetching data:", error);
        }
        finally {
            setLoading(false)
        }
    };
    const fetchUpdatedData = async () => {
        try {
            const response = await api.get(`${BACKEND_BASE_URL}/api/channel-links/?page=${currentPage}&page_size=${itemsPerPage}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const fetchedData = response.data.results;
            setTotalCount(response.data.count);

            // Default sorting (recent first)
            // const sortedData = [...fetchedData].sort((a, b) => b.id - a.id);
            setData(fetchedData);
            setFilteredData(fetchedData);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleSearchChange = (event) => {
        const query = event.target.value.toLowerCase(); // Normalize query

        setSearchQuery(query);
    };

    const handleSearch = async () => {
        setSearchLoading(true);
        try {
            const response = await axios.get(`${BACKEND_BASE_URL}/api/channel-links/`, {
                params: {
                    page: 1,
                    page_size: itemsPerPage,
                    search: searchQuery,  // Use the state here
                },
            });

            setData(response.data.results);
            setFilteredData(response.data.results);
            setTotalCount(response.data.count);
            setCurrentPage(1);
        } catch (error) {
            console.error("Error searching:", error);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSortChange = (event) => {
        const key = event?.target?.value;
        setSortKey(key);

        const sortedData = [...filteredData].sort((a, b) => {
            if (key === "entry_date_desc") {
                return b.id - a.id; // Sort by date (recent first)
            } else if (key === "entry_date_asc") {
                return a.id - b.id; // Sort by ID (oldest first)
            } else if (key === "alphabetical_asc") {
                // Sort alphabetically A-Z
                const aValue = `${a.state || ""} ${a.county || ""} ${a.municipality_name || ""
                    }`.toLowerCase();
                const bValue = `${b.state || ""} ${b.county || ""} ${b.municipality_name || ""
                    }`.toLowerCase();
                return aValue.localeCompare(bValue);
            } else if (key === "alphabetical_desc") {
                // Sort alphabetically Z-A
                const aValue = `${a.state || ""} ${a.county || ""} ${a.municipality_name || ""
                    }`.toLowerCase();
                const bValue = `${b.state || ""} ${b.county || ""} ${b.municipality_name || ""
                    }`.toLowerCase();
                return bValue.localeCompare(aValue);
            }
            return 0; // Default
        });

        setFilteredData(sortedData);
    };

    const handleEdit = (updatedData) => {

        fetchUpdatedData();

    };



    const handleFileUpload = async (event) => {

        if (isDeleting) return; // Prevent upload if still deleting

        const file = event?.target?.files[0];


        if (!file) {
            toast.error("No file selected.");
            setUploadStatus("error");
            setUploadMessage("No file selected.");
            return;
        }

        setLoading(true);
        setUploadStatus(null);  // Reset any previous status
        setUploadMessage("");

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await axios.post(`${BACKEND_BASE_URL}/api/upload-file/`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data.success) {
                toast.success("File uploaded successfully!");
                setUploadStatus("success");
                setUploadMessage("File uploaded successfully.");
                fetchUpdatedData();
            } else {
                toast.warn(response.data.error || "Partial errors occurred during upload.");
                setUploadStatus("error");
                setUploadMessage(response.data.error || "Partial errors occurred during upload.");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            toast.error("Failed to upload the file.");
            setUploadStatus("error");
            setUploadMessage("Failed to upload the file.");
        } finally {
            setLoading(false);
        }
    };

    const fileInputRef = React.useRef(null);

    const handleDelete = async (row) => {
        setIsDeleting(true);
        try {
            await axios.delete(`${BACKEND_BASE_URL}/api/delete-channel-data/${row.id}/`, {
                params: { channel_type: "Both" }
            });
            toast.success("Deleted successfully");
            // Remove the deleted row from state instead of refetching
            setData(prevData => prevData.filter(item => item.id !== row.id));
            setFilteredData(prevFilteredData => prevFilteredData.filter(item => item.id !== row.id));

            // Reset file input after deletion (to avoid stale references)
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }


        } catch (error) {
            toast.error("Error deleting data.");
            console.error("Error deleting data:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" flexDirection="column" >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography style={{ fontWeight: "bold" }} variant="h4">
                                Manage Bulk Import
                            </Typography>
                            <Box display="flex" alignItems="center">
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    style={{ marginRight: "10px", minWidth: "150px" }}
                                >
                                    <InputLabel>Sort By</InputLabel>
                                    <Select value={sortKey} onChange={handleSortChange} label="Sort By">
                                        <MenuItem value="entry_date_desc">Latest Entry</MenuItem>
                                        <MenuItem value="entry_date_asc">Oldest Entry</MenuItem>
                                        <MenuItem value="alphabetical_asc">
                                            A-Z (State, County, Town)
                                        </MenuItem>
                                        <MenuItem value="alphabetical_desc">
                                            Z-A (State, County, Town)
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                {/* Search Input */}
                                <TextField
                                    placeholder="Search..."
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    InputProps={{
                                        startAdornment: (
                                            <FontAwesomeIcon
                                                icon={faMagnifyingGlass}
                                                style={{ marginRight: "8px", color: "#555" }}
                                            />
                                        ),
                                    }}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                    style={{ marginRight: "10px" }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    style={{
                                        marginRight: "10px",
                                        backgroundColor: "#0B194A",
                                        color: "white",
                                    }}
                                    disabled={searchLoading}
                                >
                                    {searchLoading ? <CircularProgress size={20} style={{ color: "white" }} /> : "Search"}
                                </Button>
                                <Button
                                    variant="contained"
                                    component="label"
                                    style={{
                                        marginRight: "10px",
                                        backgroundColor: "#0B194A",
                                        color: "white",
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                    disabled={loading || isDeleting} // Disable when deleting or loading
                                >
                                    {loading ? <CircularProgress size={20} style={{ color: "white", marginRight: 8 }} /> : null}
                                    Upload CSV/Excel
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv, .xlsx, .xls"
                                        hidden
                                        onChange={handleFileUpload}
                                    />
                                </Button>
                            </Box>
                        </Box>

                        {/* New live cities count here */}
                        <Typography variant="subtitle1" style={{ color: "black", marginTop: "5px", fontWeight: "bold" }}>
                            Total Live Cities: {liveCitiesCount?.toLocaleString()}
                        </Typography>
                        <Box display="flex" justifyContent="flex-end" marginTop="20px">
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{ minWidth: "220px" }}
                            >
                                <InputLabel id="api-key-label">Select API Key</InputLabel>
                                <Select
                                    labelId="api-key-label"
                                    value={selectedApiKey}
                                    onChange={(e) => setSelectedApiKey(e?.target?.value)}
                                    label="Select API Key"
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: "bottom",
                                            horizontal: "left",
                                        },
                                        transformOrigin: {
                                            vertical: "top",
                                            horizontal: "left",
                                        },
                                        getContentAnchorEl: null, // prevents the default anchoring behavior
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                                marginTop: 8,
                                            },
                                        },
                                    }}
                                >
                                    {apiKeys.length > 0 ? (
                                        apiKeys.map((keyObj, index) => (
                                        <MenuItem key={index} value={keyObj?.api_key}>
                                            {keyObj?.name || keyObj?.api_key}
                                        </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>No API keys available</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Box>




                    </Box>

                </Grid>

                <Grid item xs={12}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableComponent
                            data={filteredData}
                            mappedData={mappedData}
                            handleDelete={handleDelete}
                            handleEdit={handleEdit}
                            loading={loading}
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            api={selectedApiKey}
                        />
                    )}
                </Grid>
                <Box display="flex" justifyContent="center" alignItems={"center"} margin={"auto"} mb={2}>
                    <Pagination
                        count={Math.ceil(totalCount / itemsPerPage)}
                        page={currentPage}
                        onChange={(e, value) => setCurrentPage(value)}
                        color="primary"
                    />
                </Box>
            </Grid>
        </>
    );
}
